import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import conditionHelper from '../../../infrastructure/helpers/functions/condition/conditionHelper';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import Assignment from '../../../types/functions/Assignment/Assignment';
import Condition from '../../../types/functions/Condition/Condition';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import Operand from '../../../types/functions/Operand/Operand';
import locationType from '../../../types/functions/Location/LocationType';

const isLookup = (operand: Operand | undefined | null, lookupNumber: number): boolean =>
    !!operand && operand.lookupNumber === lookupNumber;

const followArguments = (root: Operand | undefined | null, argumentIndexes: number[]): Operand | undefined => {

    let current = root || undefined;

    for (const index of argumentIndexes) {
        current = (current?.arguments as Operand[] | undefined)?.[index];

        if (!current) {
            return undefined;
        }
    }

    return current;
};

/**
 * Finds the lookup operand inside an operand tree (e.g. a function with nested arguments).
 * The clicked path (argumentIndexes) is followed first; when it does not lead to the lookup,
 * the whole tree is searched.
 */
const findLookupOperand = (root: Operand | undefined | null, argumentIndexes: number[] | undefined, lookupNumber: number): Operand | undefined => {

    if (!root) {
        return undefined;
    }

    let byPath = followArguments(root, argumentIndexes || []);

    if (isLookup(byPath, lookupNumber)) {
        return byPath;
    }

    return operandHelper.asCollection(root).find(x => isLookup(x, lookupNumber));
};

const findLookupOperandInCondition = (conditions: Condition[], location: ReportLogicLocation, operandNumber: number, lookupNumber: number): Operand | undefined => {

    let targetCondition = conditions.find(c => c.number === location.conditionNumber);

    if (!targetCondition) {
        return undefined;
    }

    let root = operandNumber === 1 ? targetCondition.operand1 : targetCondition.operand2;

    return findLookupOperand(root, location.argumentIndexes, lookupNumber);
};

/**
 * Finds the lookup operand in the clicked assignment branch: in the clicked condition operand,
 * otherwise in the assignment value, otherwise anywhere in the branch.
 */
const findLookupOperandInAssignment = (assignments: Assignment[], location: ReportLogicLocation, operandNumber: number, lookupNumber: number): Operand | undefined => {

    let assignment = assignments.find(x => x.number === location.assignmentNumber);

    if (!assignment) {
        return undefined;
    }

    let conditions = conditionHelper.asCollection(assignment.condition as Condition);

    let isValue = location.pieceOfCode === locationType.pieceOfCode.assignmentValue;

    if (!isValue && conditions.some(c => c.number === location.conditionNumber)) {
        return findLookupOperandInCondition(conditions, location, operandNumber, lookupNumber);
    }

    return findLookupOperand(assignment.value, location.argumentIndexes, lookupNumber) ||
        assignmentHelper.getAllOperands(assignment).find(x => isLookup(x, lookupNumber));
};

/**
 * Finds the lookup operand in an operand list (e.g. aggregation group by); the first index selects the list item.
 */
const findLookupOperandInList = (operands: Operand[], location: ReportLogicLocation, lookupNumber: number): Operand | undefined => {

    let [index, ...rest] = location.argumentIndexes || [];

    if (index === undefined) {
        return undefined;
    }

    return findLookupOperand(operands?.[index], rest, lookupNumber);
};

export { findLookupOperand, findLookupOperandInCondition, findLookupOperandInAssignment, findLookupOperandInList };
